import { useState } from "react";
import { Box, Collapse, Container, Link, Paper, Stack, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTenant } from "../../contexts/TenantContext";
import useRevealOnScroll from "../../hooks/useRevealOnScroll";
import { sectionSx } from "./sectionStyles";
import { gradientTextSx, sectionTitleSx, tint } from "../../utils/textStyles";
import { isDarkTheme } from "../../utils/themeTraits";
import { SECTION_SCROLL_MARGIN } from "../../layouts/layoutMetrics";

// A tenant marks where the copy may be folded by dropping this character into `paragraphs`.
// Everything before it stays visible, the rest sits behind a "mutass többet" link. Without
// the marker the whole text is shown, exactly as before.
const FOLD_MARKER = "§";

// A line opening with a dash or a bullet is a list item. It is how the tenants already write
// their lists into `paragraphs` ("Mit kínálunk?\n– …\n– …"), so no new config shape is needed.
const BULLET = /^\s*[–—\-•]\s+/;

const FALLBACK_ACCENT = "#000";

const stripMarkers = (text) => text.split(FOLD_MARKER).join("").trim();

const splitAtMarker = (paragraphs) => {
  const markerIndex = paragraphs.findIndex((paragraph) => paragraph.includes(FOLD_MARKER));

  if (markerIndex === -1) {
    return { preview: paragraphs, folded: [] };
  }

  const marked = paragraphs[markerIndex];
  const cut = marked.indexOf(FOLD_MARKER);

  return {
    preview: [...paragraphs.slice(0, markerIndex), marked.slice(0, cut)]
      .map(stripMarkers)
      .filter(Boolean),
    folded: [marked.slice(cut + FOLD_MARKER.length), ...paragraphs.slice(markerIndex + 1)]
      .map(stripMarkers)
      .filter(Boolean),
  };
};

/**
 * One paragraph as the paper theme lays it out. A paragraph with at least two dash lines is a
 * list — its other lines are the list's heading — and anything else is prose. Where it stands
 * decides how prose looks: the opening paragraph is the lead, a closing one after a list is a
 * note, and prose in between is plain text.
 */
const toBlock = (paragraph, index, all) => {
  const lines = paragraph.split("\n").map((line) => line.trim()).filter(Boolean);
  const items = lines.filter((line) => BULLET.test(line)).map((line) => line.replace(BULLET, ""));

  if (items.length >= 2) {
    const heading = lines.filter((line) => !BULLET.test(line)).join(" ");
    return { kind: "list", heading, items };
  }

  if (index === 0) {
    return { kind: "lead", text: paragraph };
  }

  const isLast = index === all.length - 1;
  const followsList = all.slice(0, index).some((other) => other.split("\n").filter((line) => BULLET.test(line)).length >= 2);
  return { kind: isLast && followsList ? "note" : "text", text: paragraph };
};

const LeadBlock = ({ text }) => (
  <Typography
    align="center"
    sx={{
      whiteSpace: "pre-line",
      fontSize: { xs: 19, md: 22 },
      fontWeight: 500,
      lineHeight: 1.6,
      maxWidth: 760,
      // Not `mx: auto`: the Stack around it zeroes its children's margins.
      alignSelf: "center",
    }}>
    {text}
  </Typography>
);

const TextBlock = ({ text }) => (
  <Typography fontSize={18} lineHeight={1.7} sx={{ whiteSpace: "pre-line" }}>
    {text}
  </Typography>
);

const ListBlock = ({ heading, items, accent }) => (
  <Box
    sx={{
      backgroundColor: tint(accent, 0.05),
      border: `1px solid ${tint(accent, 0.14)}`,
      borderRadius: 3,
      p: { xs: 2.5, md: 3.5 },
    }}>
    {heading && (
      <Typography component="h3" fontSize={20} fontWeight="bold" mb={2.5}>
        {heading}
      </Typography>
    )}
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        m: 0,
        p: 0,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        columnGap: 3,
        rowGap: 1.75,
      }}>
      {items.map((item, index) => (
        <Stack key={index} component="li" direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 24,
              height: 24,
              mt: "2px",
              borderRadius: "50%",
              backgroundColor: accent,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography fontSize={17} lineHeight={1.5}>
            {item}
          </Typography>
        </Stack>
      ))}
    </Box>
  </Box>
);

const NoteBlock = ({ text, accent }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="flex-start"
    sx={{
      backgroundColor: tint(accent, 0.06),
      borderLeft: `3px solid ${accent}`,
      borderRadius: "0 12px 12px 0",
      px: { xs: 2, md: 3 },
      py: 2,
    }}>
    <InfoOutlinedIcon aria-hidden sx={{ color: accent, mt: "2px" }} />
    <Typography fontSize={17} lineHeight={1.6} sx={{ whiteSpace: "pre-line" }}>
      {text}
    </Typography>
  </Stack>
);

const renderPaperBlock = (block, index, accent) => {
  switch (block.kind) {
    case "lead":
      return <LeadBlock key={index} text={block.text} />;
    case "list":
      return <ListBlock key={index} heading={block.heading} items={block.items} accent={accent} />;
    case "note":
      return <NoteBlock key={index} text={block.text} accent={accent} />;
    default:
      return <TextBlock key={index} text={block.text} />;
  }
};

// Two soft discs of the tenant's accent behind the card's corners: enough to lift paper off
// plain white without a colour of the page's own.
const paperDecorSx = (accent) => ({
  position: "relative",
  overflow: "hidden",
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    borderRadius: "50%",
    pointerEvents: "none",
  },
  "&::before": {
    width: 320,
    height: 320,
    top: -140,
    right: -120,
    background: `radial-gradient(circle, ${tint(accent, 0.12)} 0%, ${tint(accent, 0)} 70%)`,
  },
  "&::after": {
    width: 240,
    height: 240,
    bottom: -120,
    left: -100,
    background: `radial-gradient(circle, ${tint(accent, 0.09)} 0%, ${tint(accent, 0)} 70%)`,
  },
  "& > *": { position: "relative" },
});

const AboutSection = () => {
  const { displayConfig, homePage } = useTenant();
  // "Rólunk" rises from below; the sections to either side of it come in sideways. It is
  // taller than the viewport, so it reveals with no threshold at all — waiting for 15% of
  // it to clear the fold would hold the animation back until the user has scrolled past
  // the title.
  const { ref, revealSx } = useRevealOnScroll({
    direction: "up",
    distance: 80,
    threshold: 0,
    rootMargin: "0px",
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const about = homePage?.about;
  const paragraphs = about?.paragraphs ?? [];
  const { preview, folded } = splitAtMarker(paragraphs);

  // Optional section: a tenant that leaves `about.paragraphs` empty gets no section at all.
  if (paragraphs.length === 0) {
    return null;
  }

  const isPaper = !isDarkTheme(displayConfig);
  const accent = displayConfig?.primaryColor || FALLBACK_ACCENT;

  // Paper lays the copy out in blocks (lead, list cards, a closing note); the dark theme keeps
  // its plain paragraphs. The blocks are worked out over the whole text, fold or no fold, so a
  // paragraph looks the same whichever side of the marker it lands on.
  const blocks = [...preview, ...folded].map(toBlock);
  const renderAt = (paragraph, index) => (isPaper
    ? renderPaperBlock(blocks[index], index, accent)
    : (
      <Typography key={index} fontSize={18} sx={{ whiteSpace: "pre-line" }}>
        {paragraph}
      </Typography>
    ));
  const spacing = isPaper ? 4 : 3;

  return (
    <Container maxWidth="xl">
      <Paper ref={ref} id="about" elevation={0}
        sx={{
          ...sectionSx(displayConfig),
          ...(isPaper ? paperDecorSx(accent) : {}),
          scrollMarginTop: SECTION_SCROLL_MARGIN,
          my: { xs: 3, lg: 4, xl: 5 },
          py: isPaper ? { xs: 5, md: 7 } : 4,
          px: { xs: 3, sm: 5 },
          display: "flex", flexDirection: "column", alignItems: "center",
          ...revealSx }}>
        <Typography variant="h4" fontWeight="bold" align="center" mb={isPaper ? 0 : 4}
          sx={sectionTitleSx(displayConfig)}>
          {about?.title || "Rólunk"}
        </Typography>

        {isPaper && (
          <Box aria-hidden
            sx={{ width: 56, height: 3, borderRadius: 2, backgroundColor: accent, mt: 2, mb: { xs: 4, md: 5 } }} />
        )}

        <Box width={{ xs: "100%", md: "85%", lg: isPaper ? "75%" : "70%" }}>
          <Stack spacing={spacing}>
            {preview.map((paragraph, index) => renderAt(paragraph, index))}
          </Stack>

          {folded.length > 0 && (
            <>
              <Collapse in={isExpanded}>
                <Stack spacing={spacing} mt={spacing}>
                  {folded.map((paragraph, index) => renderAt(paragraph, preview.length + index))}
                </Stack>
              </Collapse>

              <Link
                component="button"
                type="button"
                onClick={() => setIsExpanded((current) => !current)}
                sx={{
                  mt: 2,
                  fontSize: 18,
                  fontWeight: "bold",
                  cursor: "pointer",
                  ...gradientTextSx(displayConfig, displayConfig?.primaryColor),
                  textDecorationColor: displayConfig?.primaryColor,
                }}>
                {isExpanded ? "mutass kevesebbet" : "mutass többet"}
              </Link>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default AboutSection;
